import type { DataProvider, Identifier, RaRecord } from "ra-core";
import type {
  CampaignAction,
  ConsentBasis,
  SegmentCounts,
  SegmentEntity,
  SegmentFilter,
} from "@/components/gavion/domain/email";
import type { SequenceStepStat } from "../sequences/stepStats";
import type { SequencePerformance } from "../sequences/performance";

export type { SequenceStepStat } from "../sequences/stepStats";
export type { SequencePerformance } from "../sequences/performance";
export type {
  CampaignAction,
  CampaignStatus,
} from "@/components/gavion/domain/email";

export type CampaignRecord = RaRecord & Record<string, unknown>;
export type EnrollmentRecord = RaRecord & Record<string, unknown>;
export type ConsentRecordRow = RaRecord & Record<string, unknown>;
export type ProviderStatus = {
  productionAccess: boolean | null;
  fake: boolean;
  checkedAt: string | null;
  /** false while Amazon SES has no credentials (`ses_not_configured`); absent = unknown (demo). */
  configured?: boolean;
};
export type ConsentInputUi = {
  email: string;
  basis: ConsentBasis;
  evidence: string | null;
  obtainedAt: string;
  companyId?: Identifier | null;
  contactId?: Identifier | null;
};
/** GAVION campaign-fixes: where a lead's company email is conspicuously published (consents.evidence source). */
export const PUBLICATION_SOURCES = [
  "company_website",
  "google_listing",
  "directory",
] as const;
export type PublicationSource = (typeof PUBLICATION_SOURCES)[number];
/** Twin of record_publication_consents' URL check (94_): http(s), a host, no whitespace, ≤ 2000 characters. */
export const isPublicationUrl = (url: string): boolean =>
  /^https?:\/\/[^/\s]+/i.test(url) && !/\s/.test(url) && url.length <= 2000;
export type PublicationConsentInput = {
  companyIds: Identifier[];
  source: PublicationSource;
  /** Optional http(s) link to the page showing the address. */
  url: string | null;
  /** UI entry point, stored in the evidence: the leads list (bulk, the default) or the lead page (single). */
  via?: PublicationVia;
};
export type PublicationVia = "bulk" | "single";
export type PublicationConsentResult = {
  recorded: number;
  skipped: number;
  suppressed: number;
  /** Addresses whose recorded basis is « none » (a deliberate refusal), never overwritten. */
  refused: number;
  noEmail: number;
};
/** GAVION video-campaign: « Ajouter à la campagne » (enroll_companies_in_sequence, 98_). */
export type BulkEnrollInput = {
  sequenceId: Identifier;
  companyIds: Identifier[];
};
/** Per compagnie: enrolled, or why not (no assigned / disabled rep, Outlook not connected, not the rep's own…). */
export type BulkEnrollResult = {
  enrolled: number;
  already: number;
  /** « Ne pas appeler », « Client », « Pas intéressé », « Démo réservée », « En pipeline »: never enrolled. */
  status: number;
  noEmail: number;
  suppressed: number;
  noConsent: number;
  noRep: number;
  notConnected: number;
  notOwn: number;
  failed: number;
};
export const BULK_ENROLL_KEYS = [
  "enrolled",
  "already",
  "status",
  "noEmail",
  "suppressed",
  "noConsent",
  "noRep",
  "notConnected",
  "notOwn",
  "failed",
] as const satisfies readonly (keyof BulkEnrollResult)[];
/** GAVION sequence-auto-enroll: the « Inscription automatique » setting (sequences.auto_enroll, 99_). */
export type AutoEnrollSettings = {
  segmentId: Identifier;
  /** 1–200 (also capped at run time by the rep's daily Outlook cap). */
  perDay: number;
  paused: boolean;
};
/** Why the daily run would skip the sequence (private.sequence_auto_enroll_blocker, 99_). */
export const AUTO_ENROLL_BLOCKERS = [
  "sequence_disabled",
  "sequence_empty",
  "template_draft",
  "attachments_missing",
  "owner_inactive",
  "not_connected",
  "segment_not_found",
  "segment_bad_entity",
] as const;
export type AutoEnrollBlocker = (typeof AUTO_ENROLL_BLOCKERS)[number];
/** sequence_auto_enroll_preview (99_). */
export type AutoEnrollPreview = {
  /** Compagnies the run could still enrol (null: no segment, or an invalid one). */
  candidates: number | null;
  /** Automatic enrolments of the Toronto day. */
  todayEnrolled: number;
  /** Toronto date (YYYY-MM-DD) of the last run, or null. */
  lastRun: string | null;
  /** null when the setting is off. */
  paused: boolean | null;
  /** Why it is paused: the run found the segment spent, or an admin paused it. */
  pausedReason: "segment_empty" | "manual" | null;
  blocker: AutoEnrollBlocker | null;
  /** The owner's sends still possible today under the per-mailbox cap. */
  capLeft: number;
};
export type EnrollInput = {
  sequenceId: Identifier;
  companyId: Identifier;
  contactId?: Identifier | null;
  email: string;
};

/** `warning` is set for kinds without a CASL footer; `missing` lists variables left empty. */
export type SendTestResult = {
  ok: true;
  warning?: "no_casl_footer";
  missing?: string[];
};

export interface EmailMethods {
  emailPreviewSegment(
    entity: SegmentEntity,
    filter: SegmentFilter,
  ): Promise<SegmentCounts>;
  emailScheduleCampaign(
    campaignId: Identifier,
    at: string | null,
  ): Promise<CampaignRecord>;
  emailSetCampaignStatus(
    campaignId: Identifier,
    action: CampaignAction,
  ): Promise<CampaignRecord>;
  emailEnroll(
    i: EnrollInput,
  ): Promise<{ enrollment: EnrollmentRecord; alreadyEnrolled: boolean }>;
  /** GAVION video-campaign: enrols the selected compagnies (admins any, a rep his own); counts per outcome. */
  emailEnrollCompanies(i: BulkEnrollInput): Promise<BulkEnrollResult>;
  emailUnenroll(enrollmentId: Identifier): Promise<void>;
  /** GAVION sequence-auto-enroll (admins): saves (owner = the caller) or, with null, turns off the setting. */
  emailSetSequenceAutoEnroll(
    sequenceId: Identifier,
    settings: AutoEnrollSettings | null,
  ): Promise<void>;
  /** GAVION sequence-auto-enroll (admins): the status line; segmentId previews a segment not saved yet. */
  emailSequenceAutoEnrollPreview(
    sequenceId: Identifier,
    segmentId?: Identifier | null,
  ): Promise<AutoEnrollPreview>;
  emailRecordConsent(i: ConsentInputUi): Promise<ConsentRecordRow>;
  /**
   * GAVION campaign-fixes (admins): implied_conspicuous_publication for the leads' company email
   * (RPC record_publication_consents); suppressed addresses and valid consents are left alone.
   */
  emailRecordPublicationConsents(
    i: PublicationConsentInput,
  ): Promise<PublicationConsentResult>;
  emailSendTest(i: {
    templateId: Identifier;
    companyId?: Identifier | null;
  }): Promise<SendTestResult>;
  emailSeedDefaultTemplates(): Promise<{ inserted: number }>;
  /**
   * GAVION X4: installs the default templates (drafts) if needed, then the starter sequences (DISABLED,
   * idempotent on starter_key). `missing`: starter keys not created because a template is absent.
   */
  emailInstallStarterSequences(): Promise<StarterInstallResult>;
  emailProviderStatus(): Promise<ProviderStatus>;
  /** GAVION open-pixel: per-step sent / opened / clicked / replied (all sequences when no id). */
  emailSequenceStepStats(
    sequenceId?: Identifier | null,
  ): Promise<SequenceStepStat[]>;
  /**
   * GAVION sequence-performance: the « Performance » view of one sequence (RPC sequence_performance, security
   * invoker: a rep counts what he can read).
   */
  emailSequencePerformance(
    sequenceId: Identifier,
  ): Promise<SequencePerformance>;
  /** GAVION outlook-parity: confirm (= the link unsubscribe) or ignore a weak stop request → its status. */
  emailDecideStopRequest(
    requestId: Identifier,
    decision: "confirm" | "ignore",
  ): Promise<string>;
}
export type EmailDataProvider = DataProvider & EmailMethods;
export type StarterInstallResult = {
  inserted: number;
  skipped: number;
  missing: string[];
};

export class EmailApiError extends Error {
  readonly code: string;
  constructor(code: string) {
    super(code);
    this.name = "EmailApiError";
    this.code = code;
  }
}

/** Stable codes (batch-5 global constraints); each maps to gavion.email.common.errors.<code>. */
export const KNOWN_ERROR_CODES = [
  "forbidden",
  "campaign_not_found",
  "campaign_not_draft",
  "casl_ack_required",
  "postal_address_missing",
  "template_archived",
  "template_draft",
  "from_domain_invalid",
  "daily_cap_invalid",
  "daily_cap_too_high",
  "invalid_transition",
  "campaign_not_editable",
  "campaign_must_start_draft",
  "status_via_rpc_only",
  "no_email",
  "suppressed",
  "no_consent",
  "sequence_not_found",
  "sequence_disabled",
  "sequence_empty",
  "invalid_email",
  "invalid_basis",
  "evidence_required",
  "invalid_date",
  "future_date",
  "segment_invalid",
  "segment_not_found",
  "segment_unknown_field",
  "segment_bad_condition",
  "segment_bad_entity",
  "template_missing",
  "invalid_token",
  "rate_limited",
  "invalid_link",
  // B5-T23: gavion-ses-send answers (test send)
  "ses_not_configured",
  "send_failed",
  "template_invalid",
  "company_not_found",
  // GAVION outlook-parity: decide_stop_request
  "stop_request_not_found",
  "stop_request_gone",
  // GAVION X4: install_starter_sequences
  "invalid_starters",
  // GAVION campaign-fixes: record_publication_consents
  "invalid_source",
  "invalid_url",
  "invalid_via",
  "no_companies",
  "too_many",
  // GAVION video-campaign: enroll_companies_in_sequence / enroll_in_sequence
  "attachments_missing",
  "already_enrolled",
  "casl_footer_missing",
  "unsubscribe_not_configured",
  // GAVION sequence-auto-enroll: set_sequence_auto_enroll
  "per_day_invalid",
  "auto_enroll_invalid",
] as const;
export type EmailErrorCode = (typeof KNOWN_ERROR_CODES)[number] | "unknown";

/** Maps any thrown value (EmailApiError, PostgREST P0001 error, string) to a known code. */
export function toEmailErrorCode(e: unknown): EmailErrorCode {
  const raw =
    e instanceof EmailApiError
      ? e.code
      : typeof e === "object" && e !== null && "message" in e
        ? String((e as { message: unknown }).message)
        : typeof e === "string"
          ? e
          : "";
  const code = raw.split(":")[0].trim();
  return (KNOWN_ERROR_CODES as readonly string[]).includes(code)
    ? (code as EmailErrorCode)
    : "unknown";
}
